import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { RootState } from '../index'

// Define base API URL - this should be configured via environment variables
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api/v1'

// Base query with authentication
const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState
    const token = state.auth.token

    // Add authorization header if token exists
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }

    // Add common headers
    headers.set('content-type', 'application/json')

    return headers
  },
})

// Enhanced base query with error handling and token refresh
const baseQueryWithReauth = async (
  args: Parameters<typeof baseQuery>[0],
  api: Parameters<typeof baseQuery>[1],
  extraOptions: Parameters<typeof baseQuery>[2]
) => {
  let result = await baseQuery(args, api, extraOptions)

  // Handle 401 errors (unauthorized)
  if (result.error && result.error.status === 401) {
    // Token might be expired, trigger logout
    api.dispatch({ type: 'auth/logout' })
  }

  return result
}

// Create the main API slice
export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'User',
    'Analysis',
    'SBOM',
    'Vulnerability',
    'Compliance',
    'Report',
    'Dashboard',
  ],
  endpoints: () => ({}), // Individual endpoints will be defined in separate files
})

// Export hooks for queries and mutations
// Individual endpoint hooks will be exported from their respective files
